// Compact radar chart for Big Five candidate vs team baseline.
// Pure SVG, no dependencies. All scores are 0-100.

const TRAIT_LABEL = {
  openness: "Openness",
  conscientiousness: "Consc.",
  extraversion: "Extraversion",
  agreeableness: "Agreeable",
  emotional_stability: "Stability",
};

// Fixed trait order — clockwise from top so shape is comparable across candidates.
const TRAITS = ["openness", "conscientiousness", "extraversion", "emotional_stability", "agreeableness"];

export default function TraitRadar({ candidate, team, size = 260, className = "" }) {
  const c = size / 2;
  const r = c - 32; // leave room for labels
  const n = TRAITS.length;

  // Angle for each vertex, starting from top (- pi/2), going clockwise
  const angles = TRAITS.map((_, i) => -Math.PI / 2 + (2 * Math.PI * i) / n);

  const pointFor = (score, angle) => {
    const rr = (Math.max(0, Math.min(100, score || 0)) / 100) * r;
    return [c + rr * Math.cos(angle), c + rr * Math.sin(angle)];
  };

  const polyPoints = (scores) =>
    TRAITS.map((t, i) => pointFor(scores?.[t] ?? 0, angles[i]).join(",")).join(" ");

  const gridLevels = [0.25, 0.5, 0.75, 1];

  return (
    <div className={`inline-block ${className}`} data-testid="trait-radar">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Big Five radar chart">
        {/* Grid rings */}
        {gridLevels.map((lvl, i) => (
          <polygon
            key={i}
            fill="none"
            stroke={i === gridLevels.length - 1 ? "#d4d4d8" : "#e5e5ea"}
            strokeWidth="1"
            points={TRAITS.map((_, k) => {
              const rr = r * lvl;
              return `${c + rr * Math.cos(angles[k])},${c + rr * Math.sin(angles[k])}`;
            }).join(" ")}
          />
        ))}
        {/* Axes */}
        {TRAITS.map((t, i) => {
          const [x, y] = pointFor(100, angles[i]);
          return <line key={t} x1={c} y1={c} x2={x} y2={y} stroke="#e5e5ea" strokeWidth="1" />;
        })}

        {/* Team baseline polygon (grey fill) */}
        {team && (
          <polygon
            points={polyPoints(team)}
            fill="#a3a3a3"
            fillOpacity="0.22"
            stroke="#a3a3a3"
            strokeWidth="1.5"
            data-testid="radar-team"
          />
        )}
        {/* Candidate polygon (pink) */}
        {candidate && (
          <polygon
            points={polyPoints(candidate)}
            fill="#FF007F"
            fillOpacity="0.18"
            stroke="#FF007F"
            strokeWidth="2"
            data-testid="radar-candidate"
          />
        )}

        {/* Candidate vertices dots */}
        {candidate && TRAITS.map((t, i) => {
          const [x, y] = pointFor(candidate[t] ?? 0, angles[i]);
          return <circle key={t} cx={x} cy={y} r="3" fill="#FF007F" />;
        })}

        {/* Labels */}
        {TRAITS.map((t, i) => {
          const [x, y] = pointFor(100 + 18, angles[i]);
          const anchor = Math.abs(Math.cos(angles[i])) < 0.2 ? "middle" : (Math.cos(angles[i]) > 0 ? "start" : "end");
          return (
            <text
              key={t}
              x={x}
              y={y}
              textAnchor={anchor}
              dominantBaseline="middle"
              fontSize="10"
              fontWeight="600"
              fill="#525252"
              data-testid={`radar-label-${t}`}
            >
              {TRAIT_LABEL[t] || t}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
